import React, { useState, useEffect } from 'react';
import api from '../../../services/api';
import { DietaPersonaleDettaglio, DieteFiltri, ApiResponse } from '../../../types/diete';
import './DettaglioDieteModal.css';

interface DettaglioDieteModalProps {
  isOpen: boolean;
  onClose: () => void;
  idUser: number;
  nomeUtente: string;
  numeroDiete: number;
  filtri: DieteFiltri;
}

const DettaglioDieteModal: React.FC<DettaglioDieteModalProps> = ({
  isOpen,
  onClose,
  idUser,
  nomeUtente,
  numeroDiete,
  filtri
}) => {
  const [diete, setDiete] = useState<DietaPersonaleDettaglio[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (isOpen && idUser > 0) {
      caricaDiete();
    }
  }, [isOpen, idUser, filtri]);

  const caricaDiete = async () => {
    setLoading(true);
    setError(null);
    
    try {
      const response = await api.post<ApiResponse<DietaPersonaleDettaglio[]>>(
        `/DietePersonali/dettaglio-utente/${idUser}`,
        filtri
      );
      
      if (response.data.success) {
        setDiete(response.data.data);
      } else {
        setError(response.data.message || 'Errore nel caricamento delle diete');
      }
    } catch (error: any) {
      setError(error.response?.data?.message || 'Errore durante il caricamento');
      console.error('Errore caricamento diete:', error);
    } finally {
      setLoading(false);
    }
  };

  const formatDate = (dateString: string) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('it-IT', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric'
    });
  };

  const formatDateTime = (dateString: string) => {
    const date = new Date(dateString);
    return date.toLocaleString('it-IT', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  if (!isOpen) return null;

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal-content" onClick={(e) => e.stopPropagation()}>
        <div className="modal-header">
          <h3>Dettaglio Diete - {nomeUtente}</h3>
          <button className="modal-close" onClick={onClose}>
            ×
          </button>
        </div>

        <div className="modal-info">
          <div className="info-item">
            <span className="label">Periodo:</span>
            <span className="value">
              {formatDate(filtri.dataDa)} - {formatDate(filtri.dataA)}
            </span>
          </div>
          <div className="info-item">
            <span className="label">Totale Diete:</span>
            <span className="value badge-count">{numeroDiete}</span>
          </div>
        </div>

        <div className="modal-body">
          {loading && (
            <div className="loading-container">
              <div className="loading-spinner"></div>
              <p>Caricamento diete...</p>
            </div>
          )}

          {error && (
            <div className="error-message">
              <p>⚠️ {error}</p>
            </div>
          )}

          {!loading && !error && diete.length === 0 && (
            <div className="no-results">
              <p>Nessuna dieta trovata per questo utente nel periodo selezionato.</p>
            </div>
          )}

          {!loading && !error && diete.length > 0 && (
            <div className="diete-list">
              {diete.map((dieta, index) => (
                <div key={dieta.idDieta} className="dieta-card">
                  <div className="dieta-header">
                    <div className="dieta-title">
                      <h4>{dieta.nome || `Dieta ${dieta.idDieta}`}</h4>
                      <div className="dieta-badges">
                        {dieta.sostituzioni && <span className="badge badge-sostituzioni">Sostituzioni Attive</span>}
                        {dieta.modello && <span className="badge badge-modello">{dieta.modello}</span>}
                      </div>
                    </div>
                    <div className="dieta-id">ID: {dieta.idDieta}</div>
                  </div>

                  <div className="dieta-content">
                    {dieta.modello && (
                      <div className="dieta-section">
                        <strong>Modello:</strong>
                        <p>{dieta.modello}</p>
                      </div>
                    )}

                    {dieta.patologie && (
                      <div className="dieta-section">
                        <strong>Patologie:</strong>
                        <p>{dieta.patologie}</p>
                      </div>
                    )}

                    <div className="dieta-dates">
                      <div className="date-item">
                        <strong>Creata:</strong> {formatDateTime(dieta.dataCreazione)}
                      </div>
                      {dieta.dataModifica && (
                        <div className="date-item">
                          <strong>Modificata:</strong> {formatDateTime(dieta.dataModifica)}
                        </div>
                      )}
                    </div>

                    <div className="dieta-flags">
                      <div className="flag-item">
                        <strong>Sostituzioni:</strong> 
                        <span className={`flag-value ${dieta.sostituzioni ? 'active' : 'inactive'}`}>
                          {dieta.sostituzioni ? 'Attive' : 'Disattivate'}
                        </span>
                      </div>
                    </div>

                    {dieta.note && (
                      <div className="dieta-section">
                        <strong>Note:</strong>
                        <p>{dieta.note}</p>
                      </div>
                    )}
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>

        <div className="modal-footer">
          <button className="btn-secondary" onClick={onClose}>
            Chiudi
          </button>
        </div>
      </div>
    </div>
  );
};

export default DettaglioDieteModal;